import { UiCreate, UiRecord, UiUpdate } from '../types';
import { APP_TIMEZONE, parseStoredDatetimeParts, storedToDatetimeLocalValue } from './easternTime';


export type UiDraft = {
  has_ui: boolean;
  ui_id?: number;
  mui: boolean;
  location_of_incident: string;
  time_of_incident: string;
  description_of_incident: string;
  description_of_injury: string;
  causes_or_contributing_factors: string;
  actions_taken: string;
  document_id?: number | '';
};

export type UiApiLike = {
  list: (params: {
    visit_id?: number;
    transportation_id?: number;
    provider_id?: number;
    client_id?: number;
    mui?: boolean;
  }) => Promise<UiRecord[]>;
  create: (data: UiCreate) => Promise<UiRecord>;
  update: (id: number, data: UiUpdate) => Promise<UiRecord>;
  delete: (id: number) => Promise<unknown>;
};

export const emptyUiDraft = (): UiDraft => ({
  has_ui: false,
  mui: false,
  location_of_incident: '',
  time_of_incident: '',
  description_of_incident: '',
  description_of_injury: '',
  causes_or_contributing_factors: '',
  actions_taken: '',
});

export function toDatetimeLocalValue(iso?: string | null): string {
  return storedToDatetimeLocalValue(iso);
}

export function fromDatetimeLocalValue(value: string): string {
  if (!value) return '';
  if (value.includes('T')) {
    const [date, time] = value.split('T');
    const withSeconds = time.length === 5 ? `${time}:00` : time;
    return `${date}T${withSeconds}`;
  }
  return value;
}

export function uiDraftFromRecord(record: UiRecord): UiDraft {
  return {
    has_ui: true,
    ui_id: record.ui_id,
    mui: record.mui || false,
    location_of_incident: record.location_of_incident || '',
    time_of_incident: toDatetimeLocalValue(record.time_of_incident),
    description_of_incident: record.description_of_incident || '',
    description_of_injury: record.description_of_injury || '',
    causes_or_contributing_factors: record.causes_or_contributing_factors || '',
    actions_taken: record.actions_taken || '',
    document_id: record.document_id ?? '',
  };
}

export function uiDraftHasContent(draft: UiDraft | undefined): boolean {
  if (!draft?.has_ui) return false;
  return true;
}

export function validateUiDraft(draft: UiDraft | undefined): string | null {
  if (!draft?.has_ui) return null;
  if (!draft.location_of_incident.trim()) return 'UI location of incident is required.';
  if (!draft.time_of_incident.trim()) return 'UI time of incident is required.';
  if (!draft.description_of_incident.trim()) return 'UI description of incident is required.';
  return null;
}

function buildUiPayload(
  draft: UiDraft,
  ctx: {
    provider_id: number;
    client_id: number;
    visit_id?: number | null;
    transportation_id?: number | null;
  }
): UiCreate {
  return {
    provider_id: ctx.provider_id,
    client_id: ctx.client_id,
    visit_id: ctx.visit_id ?? null,
    transportation_id: ctx.transportation_id ?? null,
    document_id:
      draft.document_id === '' || draft.document_id == null ? null : Number(draft.document_id),
    mui: draft.mui,
    location_of_incident: draft.location_of_incident.trim(),
    time_of_incident: fromDatetimeLocalValue(draft.time_of_incident),
    description_of_incident: draft.description_of_incident.trim(),
    description_of_injury: draft.description_of_injury.trim() || null,
    causes_or_contributing_factors: draft.causes_or_contributing_factors.trim() || null,
    actions_taken: draft.actions_taken.trim() || null,
  };
}

export function buildUiCreatePayload(
  draft: UiDraft,
  ctx: {
    provider_id: number;
    client_id: number;
    visit_id?: number | null;
    transportation_id?: number | null;
  }
): UiCreate {
  return buildUiPayload(draft, ctx);
}

function formatLocalDateTime(iso?: string | null): string {
  const parts = parseStoredDatetimeParts(iso);
  if (!parts) return '';
  const d = new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  return d.toLocaleString('en-US', {
    timeZone: APP_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatLocalDate(dateStr?: string | null): string {
  if (!dateStr) return '';
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return '';
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', {
    timeZone: APP_TIMEZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Human-readable visit context for association pickers (no IDs). */
export function formatVisitAssociationLabel(visit: {
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  visit_date?: string | null;
  status?: string | null;
}): string {
  const start = formatLocalDateTime(visit.scheduled_start);
  const end = formatLocalDateTime(visit.scheduled_end);
  const dateOnly = formatLocalDate(visit.visit_date || visit.scheduled_start);
  let when = start;
  if (start && end && start !== end) {
    const endTime = visit.scheduled_end
      ? new Date(visit.scheduled_end.includes('T') ? visit.scheduled_end : visit.scheduled_end.replace(' ', 'T'))
      : null;
    const endLabel =
      endTime && !Number.isNaN(endTime.getTime())
        ? endTime.toLocaleTimeString('en-US', { timeZone: APP_TIMEZONE, hour: 'numeric', minute: '2-digit' })
        : '';
    when = endLabel ? `${start} – ${endLabel}` : start;
  } else if (!when && dateOnly) {
    when = dateOnly;
  }
  const status = visit.status ? ` (${visit.status})` : '';
  return when ? `Visit on ${when}${status}` : `Visit${status}`;
}

/** Human-readable transportation context for association pickers (no IDs). */
export function formatTransportationAssociationLabel(t: {
  transportation_date?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
}): string {
  const date = formatLocalDate(t.transportation_date || t.scheduled_start);
  const start = formatLocalDateTime(t.scheduled_start);
  const end = formatLocalDateTime(t.scheduled_end);
  let when = start;
  if (start && end && start !== end) {
    const endTime = t.scheduled_end
      ? new Date(t.scheduled_end.includes('T') ? t.scheduled_end : t.scheduled_end.replace(' ', 'T'))
      : null;
    const endLabel =
      endTime && !Number.isNaN(endTime.getTime())
        ? endTime.toLocaleTimeString('en-US', { timeZone: APP_TIMEZONE, hour: 'numeric', minute: '2-digit' })
        : '';
    when = endLabel ? `${start} – ${endLabel}` : start;
  } else if (!when && date) {
    when = date;
  }
  return when ? `Transportation on ${when}` : 'Transportation';
}

export function sortByDescTime<T>(items: T[], getTime: (item: T) => string | undefined | null): T[] {
  return [...items].sort((a, b) => {
    const ta = getTime(a) || '';
    const tb = getTime(b) || '';
    return tb.localeCompare(ta);
  });
}

export const RECENT_ASSOCIATION_LIMIT = 15;

export async function syncUiForVisit(
  api: UiApiLike,
  draft: UiDraft | undefined,
  ctx: { provider_id: number; client_id: number; visit_id: number }
): Promise<void> {
  if (!draft) return;
  if (!draft.has_ui) {
    if (draft.ui_id) {
      await api.delete(draft.ui_id);
    }
    return;
  }
  const err = validateUiDraft(draft);
  if (err) throw new Error(err);
  const payload = buildUiPayload(draft, { ...ctx, visit_id: ctx.visit_id, transportation_id: null });
  if (draft.ui_id) {
    await api.update(draft.ui_id, payload);
  } else {
    await api.create(payload);
  }
}

export async function syncUiForTransportation(
  api: UiApiLike,
  draft: UiDraft | undefined,
  ctx: { provider_id: number; client_id: number; transportation_id: number }
): Promise<void> {
  if (!draft) return;
  if (!draft.has_ui) {
    if (draft.ui_id) {
      await api.delete(draft.ui_id);
    }
    return;
  }
  const err = validateUiDraft(draft);
  if (err) throw new Error(err);
  const payload = buildUiPayload(draft, {
    ...ctx,
    visit_id: null,
    transportation_id: ctx.transportation_id,
  });
  if (draft.ui_id) {
    await api.update(draft.ui_id, payload);
  } else {
    await api.create(payload);
  }
}
